import { decode } from 'js-base64';
import { NotificationCard } from '../../components/Card';
import {
  Avatar,
  Button,
  Flex,
  Icon,
  Center,
  useDisclosure,
  Link,
} from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';
import { useMarkAsReadMutation } from '@/store/query/notification';
import { LayersThree02 } from '@kula-ai/icons/dual-tone/LayersThree02';
import { NotificationTime } from '../../components/Time';
import {
  RequisitionDetailsSection,
  RequisitionDetailsList,
} from '../requisition-notifications/components';
import { KText } from '@/ui-library/components';
import { jobsEmploymentsOptions } from '@/pages/ats-job-creation/tabs/JobInformation/jobInfoHelpers';

// Constants
const WORKPLACE_OPTIONS = {
  office: 'On-Site',
  remote: 'Remote',
  hybrid: 'Hybrid',
};

const ENTITY_TYPES = {
  JOB: 'ats_job',
  OFFER: 'offer',
};

const APPROVAL_TEXT = {
  FINAL: (stepNo) => `Step ${stepNo} (Final approval)`,
  ANY: (stepNo) => `Step ${stepNo} (Any approver can approve)`,
  ALL_APPROVED: 'All approvers approved',
  PENDING: (count) =>
    `${count} pending ${count === 1 ? 'approval' : 'approvals'}`,
};

// Helper functions
const formatEmploymentType = (type) => {
  if (!type) return '';
  const option = jobsEmploymentsOptions.find((opt) => opt.value === type);
  return option?.label || type;
};

const formatWorkplace = (place) => {
  if (!place) return '';
  return WORKPLACE_OPTIONS[place] || place;
};

const formatRemainingSteps = (count) => {
  if (count === undefined || count === null) return null;
  return `${count} ${count === 1 ? 'step' : 'steps'}`;
};

// Reusable UI Components
const PersonAvatar = ({ name, avatarUrl }) => (
  <Flex alignItems="center" gap="6px">
    <Avatar size="2xs" borderRadius="4px" src={avatarUrl} name={name} />
    <KText size="sm-medium">{name}</KText>
  </Flex>
);

const CandidateLink = ({ name, avatarUrl, url }) => (
  <Link
    target="_blank"
    w="100%"
    display="flex"
    alignItems="center"
    gap="6px"
    href={url}
  >
    <Avatar size="2xs" borderRadius="4px" src={avatarUrl} name={name} />
    <KText
      maxW="100%"
      enableTooltip={true}
      isTruncated
      size="sm-regular"
      _hover={{
        textDecoration: 'underline',
        color: 'primary-500',
        cursor: 'pointer',
      }}
      textTransform="capitalize"
    >
      {name}
    </KText>
  </Link>
);

// Text generation helpers
const generateApproverStatusText = (pendingVote) => {
  if (pendingVote === 0) {
    return APPROVAL_TEXT.ALL_APPROVED;
  }
  return APPROVAL_TEXT.PENDING(pendingVote);
};

const generateStepText = (isFinal, stepNo) => {
  if (isFinal) {
    return APPROVAL_TEXT.FINAL(stepNo);
  }
  return APPROVAL_TEXT.ANY(stepNo);
};

const ApprovalReviewerApprovedNotificationWrapper = ({
  notification = {},
  onClose = () => {},
  components = {},
}) => {
  const [markAsRead] = useMarkAsReadMutation();
  const { custom_attributes } = notification;
  const data = JSON.parse(decode(custom_attributes));

  const {
    job_name,
    entity_name,
    candidate_name,
    pending_vote,
    reviewer_name,
    reviewer_avatar_url,
    candidate_avatar_url,
    application_candidate_url,
    approval_step_no,
    is_final,
    entity_id,
    job_id,
    entity_type,
    reviewer_id,
    submitted_by_avatar_url,
    submitted_by_id,
    submitted_by_name,
    job_workplace,
    job_office_name,
    job_employment_type,
    job_salary_range,
    job_department_name,
    remaining_approval_steps,
  } = data || {};

  // Derived state
  const isJobApproval = entity_type === ENTITY_TYPES.JOB;
  const isApprovedOnBehalf = Boolean(
    submitted_by_id && submitted_by_id !== reviewer_id
  );
  const allApproversApproved = is_final === true && pending_vote === 0;

  const approverName = isApprovedOnBehalf ? submitted_by_name : reviewer_name;
  const approverAvatar = isApprovedOnBehalf
    ? submitted_by_avatar_url
    : reviewer_avatar_url;
  const entityDisplayName = job_name || entity_name;

  const approversLeftText = generateApproverStatusText(pending_vote);
  const stepApprovalText = generateStepText(is_final, approval_step_no);

  const history = useHistory();
  const { OfferApprovalView } = components || {};
  const offerApprovalActions = useDisclosure();

  // Build details data
  const buildDetailsData = () => {
    const details = [];

    // Approved by (only if not all approvers approved)
    if (!allApproversApproved) {
      details.push({
        label: 'Approved by',
        value: <PersonAvatar name={approverName} avatarUrl={approverAvatar} />,
      });
    }

    // Approval step info
    details.push(
      { label: 'At step', value: stepApprovalText },
      {
        label: `Approvers left in step ${approval_step_no}`,
        value: approversLeftText,
      }
    );

    // Remaining steps
    const remainingStepsValue = formatRemainingSteps(remaining_approval_steps);
    if (remainingStepsValue) {
      details.push({
        label: 'Remaining steps',
        value: remainingStepsValue,
      });
    }

    // Candidate info (offer only)
    if (!isJobApproval) {
      details.push({
        label: 'Candidate',
        value: (
          <CandidateLink
            name={candidate_name}
            avatarUrl={candidate_avatar_url}
            url={application_candidate_url}
          />
        ),
      });
    }

    // Job details
    details.push(
      { label: 'Job name', value: entityDisplayName },
      { label: 'Department', value: job_department_name },
      { label: 'Office', value: job_office_name },
      { label: 'Workplace', value: formatWorkplace(job_workplace) },
      { label: 'Work type', value: formatEmploymentType(job_employment_type) },
      { label: 'Salary range', value: job_salary_range }
    );

    return details.filter((item) => item.value);
  };

  const detailsData = buildDetailsData();

  // Handlers
  const handleGoToJob = () => {
    if (isJobApproval) {
      history.push(`/jobs/${job_id || entity_id}`);
      onClose();
      try {
        markAsRead({ id: notification?.id });
      } catch (error) {
        console.error('Notification mark as read failed');
      }
    } else {
      window.open(application_candidate_url, '_blank');
    }
  };

  // Notification message text
  const renderNotificationMessage = () => {
    if (allApproversApproved) {
      const approvalSuffix = isJobApproval
        ? ' has been approved for publishing'
        : ' offer has been approved';

      return (
        <>
          <KText as="span" fontWeight="semiBold">
            {entityDisplayName}
          </KText>
          {approvalSuffix}
        </>
      );
    }

    const actionText = isJobApproval
      ? ' has approved the '
      : ' has approved the offer for ';

    const entitySuffix = isJobApproval ? ' job' : '';

    return (
      <>
        <KText as="span" fontWeight="semiBold">
          {approverName}
        </KText>
        {actionText}
        <KText as="span" fontWeight="semiBold">
          {entityDisplayName}
        </KText>
        {entitySuffix}
        {isApprovedOnBehalf && (
          <>
            {' on behalf of '}
            <KText as="span" fontWeight="semiBold">
              {reviewer_name}
            </KText>
          </>
        )}
      </>
    );
  };

  // Button text
  const getButtonText = () => {
    return `Go to ${isJobApproval ? 'job' : 'offer'}`;
  };

  return (
    <>
      <Flex alignItems="baseline" gap="6px">
        <Center minW="34px" h="34px" borderRadius="sm" bg="pastel-2-light">
          <Icon as={LayersThree02} color="pastel-2-dark" width="16px" />
        </Center>
        <Flex flexDir="column" ml="16px" gap="8px" alignSelf="start" flex="1">
          <KText>{renderNotificationMessage()}</KText>

          <RequisitionDetailsSection title="Details">
            <RequisitionDetailsList data={detailsData} />
          </RequisitionDetailsSection>

          <Button
            w="fit-content"
            variant="primary"
            size="sm"
            onClick={handleGoToJob}
            data-test-id="go-to-job-button"
          >
            {getButtonText()}
          </Button>

          <NotificationTime notification={notification} />
        </Flex>
      </Flex>

      {offerApprovalActions?.isOpen && OfferApprovalView && (
        <OfferApprovalView
          modalActions={offerApprovalActions}
          offerId={entity_id}
          onSuccess={() => {
            offerApprovalActions?.onClose();
            onClose();
          }}
        />
      )}
    </>
  );
};

export const ApprovalReviewerApprovedNotification = (props) => {
  return (
    <NotificationCard notification={props?.notification}>
      <ApprovalReviewerApprovedNotificationWrapper {...props} />
    </NotificationCard>
  );
};
